
import moment from 'moment';
import type { Lesson } from '../types/lessons';
import type { OutTypes } from './schedules';

const firstDay = moment.utc('1970-01-05');

function fromTimeString (
  day:        number,
  timeString: string
): moment.Moment {
  const [hours, minutes] = timeString.split(':').map((time: string) => parseInt(time));
  return firstDay.clone().add(day, 'day')
    .set('hour', hours)
    .set('minute', minutes);
}

export default function (
  lessons:          Lesson[] | undefined,
  courseMap:        Map<number, OutTypes.course[]>,
  teachers:         OutTypes.teacher[] | undefined,
  importErrors:     string[],
  importWarnings:   string[],
): OutTypes.event[] {

  // create teacher set for quick lookup
  const teacherSet = new Set<string>();
  teachers?.forEach(t => teacherSet.add(t.ids!));

  const events: OutTypes.event[] = [];
  lessons?.forEach(l => {

    // Nikos takes full responsibility for this line of code
    groupScope: for (const group of l.info.groups ?? []) {
      if (!courseMap.has(group.id)) {
        const warn = `The lesson with id "${l.id}" referenced the unknown/faulty schedule_group (course) "${group.id}" and was therefore ignored.`;
        importWarnings.push(warn);
        continue groupScope;
      }
      const courses = courseMap.get(group.id);

      if (!courses?.length) {
        const warn = `The lesson with id "${l.id}" referenced the unknown/faulty schedule_group (course) "${group.id}" and was therefore ignored.`;
        importWarnings.push(warn);
        continue groupScope;
      }

      const start             = fromTimeString(l.weekday, l.info.start);
      const end               = fromTimeString(l.weekday, l.info.end);
      const preferredDuration = l.info.duration;
      const duration          = l.info.duration;

      for (const course of courses) {
        // store only teachers if they mismatch teachers of parent course
        let eventTeachers: string[] | undefined;
        if (!course.teachers || !course.teachers?.every(x => l.info.teachers?.some(y => y.id.toString() == x.to))) {
          eventTeachers = l.info.teachers?.map(x => x.id.toString());

          // ensure that the teachers exist
          for (const teacher of eventTeachers ?? []) {
            if (!teacherSet.has(teacher)) {
              const warn = `The lesson with id "${l.id}" referenced the unknown teacher "${teacher}" and was therefore ignored.`;
              importWarnings.push(warn);
              continue groupScope;
            }
          }
        }

        // location
        const location = l.info.room?.id.toString();

        events.push({
          ids:    l.id.toString(),
          start,
          end,
          preferredDuration,
          duration,
          course: course.ids,
          ...location && { inLocations: [ location ], locations: [{ locations: [ location ] }] },
          ...(eventTeachers && { teachers: eventTeachers.map((to) => ({ to })) })
        } satisfies OutTypes.event);
      }
    }
  });

  return events;
}
